import moment from 'moment';
import {
	memo,
	type PropsWithChildren,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBridgewayApi } from '@/api/BridgewayApi';
import { clearFacebookMeta } from '@/api/FacebookApi';
import { hasUnexpiredSquareConnection } from '@/api/SquareApi';
import { useImsApiContext } from '@/auth/ImsApiContext';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

import {
	type OAuthErrorMessage,
	type OAuthResultMessage,
	OAUTH_MESSAGE_TYPE,
	type OAuthSuccessMessage,
} from './oauthMessages';
import { clearOAuthState } from './oauthState';
import { UserSettingsContext } from './UserSettingsContext';
import type {
	InstagramConnectFailure,
	InstagramConnection,
	UserSettings,
	UserSettingsContextValue,
} from './userSettingsTypes';

const openAuthPopup = (path: string): void => {
	const width = 600;
	const height = 760;
	const left = window.screenX + (window.outerWidth - width) / 2;
	const top = window.screenY + (window.outerHeight - height) / 2;
	window.open(
		path,
		`oauth_${Date.now()}`,
		`width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=yes,status=no`,
	);
};

const isOAuthSuccessMessage = (
	value: unknown,
): value is OAuthResultMessage & { status: 'success' } => {
	return (
		!!value &&
		typeof value === 'object' &&
		'type' in value &&
		(value as { type?: string }).type === OAUTH_MESSAGE_TYPE &&
		'provider' in value &&
		((value as { provider?: string }).provider === 'instagram' ||
			(value as { provider?: string }).provider === 'square') &&
		'status' in value &&
		(value as { status?: string }).status === 'success'
	);
};

const isOAuthErrorMessage = (
	value: unknown,
): value is OAuthResultMessage & { status: 'error' } => {
	return (
		!!value &&
		typeof value === 'object' &&
		'type' in value &&
		(value as { type?: string }).type === OAUTH_MESSAGE_TYPE &&
		'provider' in value &&
		((value as { provider?: string }).provider === 'instagram' ||
			(value as { provider?: string }).provider === 'square') &&
		'status' in value &&
		(value as { status?: string }).status === 'error'
	);
};

export const UserSettingsProvider = memo(function UserSettingsProvider({
	children,
}: PropsWithChildren) {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const bridgeApi = useBridgeApi();
	const imsApiContext = useImsApiContext();
	const [settings, setSettings] = useState<UserSettings | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [instagramConnectFailure, setInstagramConnectFailure] =
		useState<InstagramConnectFailure | null>(null);
	// The connection card that opened the current popup, or null for a connect
	// started from the settings page. This provider sits above every route, so
	// it is still mounted when the popup reports back.
	const pendingConnectRequestIdRef = useRef<string | null>(null);
	// The popup's oauth_result carries no requestId of its own -- it's
	// matched to a card purely via pendingConnectRequestIdRef, a single
	// shared slot. A second connect opened before the first popup resolves
	// overwrites that slot, so the first popup's eventual result gets
	// attributed to the second card instead. isInstagramConnectPending lets
	// every card disable Connect while one popup is already in flight,
	// avoiding the overwrite instead of trying to disambiguate after it.
	const [isInstagramConnectPending, setIsInstagramConnectPending] =
		useState(false);
	const instagramConnection = settings?.instagram_connection ?? null;
	const squareConnection = settings?.square_connection ?? null;
	const isInstagramConnected = useMemo(() => {
		if (!instagramConnection) return false;
		// ig_token_expires_at: null means expires_at=0 (long-lived page token, never expires)
		const igTokenValid =
			instagramConnection.ig_token_expires_at === null ||
			moment(instagramConnection.ig_token_expires_at).isAfter(moment());
		// data_access_expires_at absent on older records (pre-ADO-1330 backfill) — skip check
		const dataAccessValid =
			!instagramConnection.data_access_expires_at ||
			moment(instagramConnection.data_access_expires_at).isAfter(moment());
		return igTokenValid && dataAccessValid;
	}, [instagramConnection]);
	const isSquareConnected = useMemo(() => {
		return hasUnexpiredSquareConnection(squareConnection);
	}, [squareConnection]);

	// Resolves true only when the fetch (or the no-op for a signed-out user) actually
	// landed. The revision effect below needs that distinction — see its own comment —
	// so a transient failure can be retried instead of silently consuming the signal.
	const refreshSettings = useCallback(async (): Promise<boolean> => {
		if (!userId) {
			setSettings(null);
			setIsLoading(false);
			setError(null);
			return true;
		}

		setIsLoading(true);
		try {
			const response = await bridgewayApi.get<UserSettings>(
				`/api/user/${userId}/settings`,
			);
			if (response.reply_to_email == null) {
				const loginEmail = imsApiContext?.imsApi.getImsProfile()?.email;
				if (loginEmail) {
					try {
						const patched = await bridgewayApi.patch<UserSettings>(
							`/api/user/${userId}/settings`,
							{ reply_to_email: loginEmail },
						);
						setSettings(patched);
						setError(null);
						return true;
					} catch {
						logger.warn('Failed to initialize reply_to_email');
					}
				}
			}
			setSettings(response);
			setError(null);
			return true;
		} catch (err) {
			console.error('Failed to fetch user settings', err);
			setError('Failed to load settings');
			return false;
		} finally {
			setIsLoading(false);
		}
	}, [bridgewayApi, userId, imsApiContext]);

	const patchSettings = useCallback(
		async (updates: Partial<UserSettings>): Promise<UserSettings | null> => {
			if (!userId) {
				setError('Unable to update settings: user not available');
				return null;
			}

			try {
				const response = await bridgewayApi.patch<UserSettings>(
					`/api/user/${userId}/settings`,
					updates,
				);
				setSettings(response);
				setError(null);
				return response;
			} catch (err) {
				console.error('Failed to patch user settings', err);
				setError('Failed to update settings');
				return null;
			}
		},
		[bridgewayApi, userId],
	);

	const putSettings = useCallback(
		async (updates: Partial<UserSettings>): Promise<UserSettings | null> => {
			if (!userId) {
				setError('Unable to update settings: user not available');
				return null;
			}

			try {
				const response = await bridgewayApi.put<UserSettings>(
					`/api/user/${userId}/settings`,
					updates,
				);
				setSettings(response);
				setError(null);
				return response;
			} catch (err) {
				console.error('Failed to replace user settings', err);
				setError('Failed to update settings');
				return null;
			}
		},
		[bridgewayApi, userId],
	);

	const connectInstagram = useCallback((requestId?: string) => {
		logger.info('Opening Instagram auth popup');
		// Remember which connection card opened this popup. The popup outlives
		// the card's mount, so the card cannot hold this itself.
		pendingConnectRequestIdRef.current = requestId ?? null;
		setIsInstagramConnectPending(true);
		openAuthPopup('/auth/instagram');
	}, []);

	const clearInstagramConnectFailure = useCallback(() => {
		setInstagramConnectFailure(null);
	}, []);

	const connectSquare = useCallback(() => {
		logger.info('Opening Square auth popup');
		openAuthPopup('/auth/square');
	}, []);

	const disconnectInstagram =
		useCallback(async (): Promise<UserSettings | null> => {
			logger.info('Disconnecting Instagram account');
			clearOAuthState('instagram');
			clearFacebookMeta();
			const result = await putSettings({ instagram_connection: null });
			if (!result) {
				logger.error('Failed to disconnect Instagram account');
			}
			return result;
		}, [putSettings]);

	const disconnectSquare =
		useCallback(async (): Promise<UserSettings | null> => {
			logger.info('Disconnecting Square account');
			clearOAuthState('square');
			const result = await putSettings({ square_connection: null });
			if (!result) {
				logger.error('Failed to disconnect Square account');
			}
			return result;
		}, [putSettings]);

	useEffect(() => {
		void refreshSettings();
	}, [refreshSettings]);

	// Settings live in Cosmos, so the bridge cannot push their content — it pushes a counter
	// instead. Without this, a fact established server-side (Square telling us our access was
	// withdrawn) would sit unseen until the merchant happened to reload, and they would keep
	// reading "connected" while their sales stopped arriving.
	//
	// Skipping the initial value matters: the mount fetch above has already run, and refetching
	// on first observation would double every page load.
	const settingsRevision = bridgeApi.user_state?.settings_revision ?? 0;
	// Seeded as unset, not as the value at first render. At mount user_state is
	// still null, so the revision reads 0; the snapshot then lands carrying a
	// real revision and the watcher read that as a CHANGE, refetching on top of
	// the mount fetch. Every page load paid for two settings GETs (ADO-1791).
	const lastSeenRevisionRef = useRef<number | null>(null);
	useEffect(() => {
		// Gating on user_state being non-null is what makes the null-to-snapshot
		// transition seed rather than trigger.
		if (bridgeApi.user_state == null) return;
		if (lastSeenRevisionRef.current === null) {
			lastSeenRevisionRef.current = settingsRevision;
			return;
		}
		if (settingsRevision === lastSeenRevisionRef.current) return;
		const target = settingsRevision;
		// Two revisions can overlap, and the later fetch can resolve first. Without this
		// flag the earlier fetch then advances the ref BACKWARD to its own older target,
		// and the next matching revision reads as a change that already happened.
		let cancelled = false;
		// Advance ONLY after the fetch resolves successfully (mirrors useStrategies.ts's
		// revision watcher): refreshSettings can fail on a transient error, or race the
		// backend's eventually-consistent write. Advancing the ref eagerly would let that
		// one failure consume the signal permanently — the one fact this effect exists to
		// deliver (Square access was withdrawn) would never reach an open tab, and the
		// merchant would keep seeing "connected" until they happened to reload.
		void refreshSettings().then((ok) => {
			if (cancelled) return;
			if (ok) lastSeenRevisionRef.current = target;
		});
		return () => {
			cancelled = true;
		};
	}, [bridgeApi.user_state, settingsRevision, refreshSettings]);

	useEffect(() => {
		const handleMessage = (event: MessageEvent<unknown>) => {
			if (event.origin !== window.location.origin) return;

			if (isOAuthErrorMessage(event.data)) {
				const errorMsg = event.data as OAuthErrorMessage;
				if (errorMsg.provider !== 'instagram') return;
				setInstagramConnectFailure({
					id: crypto.randomUUID(),
					step: errorMsg.code as InstagramConnectFailure['step'],
					message: errorMsg.message,
					details: errorMsg.details,
					failed_at: new Date().toISOString(),
					request_id: pendingConnectRequestIdRef.current,
				});
				setIsInstagramConnectPending(false);
				return;
			}

			if (!isOAuthSuccessMessage(event.data)) return;

			const msg = event.data as OAuthSuccessMessage;
			if (msg.provider === 'instagram') {
				setIsInstagramConnectPending(false);
			}
			void (async () => {
				if (msg.connectionData && msg.provider === 'instagram') {
					await patchSettings({
						instagram_connection: msg.connectionData as InstagramConnection,
					});
				}
				// Square is written by ui-proxy during the token exchange, not here. Two
				// writers against a settings document that is read-modify-upserted without an
				// ETag lose each other's fields, and every write that changes the merchant id
				// starts a replay of that merchant's held webhooks. This listener still
				// refreshes, so the screen updates exactly as it did before.
				await refreshSettings();
			})();
		};

		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [patchSettings, refreshSettings]);

	const value = useMemo<UserSettingsContextValue>(
		() => ({
			settings,
			isLoading,
			error,
			isInstagramConnected,
			isSquareConnected,
			instagramConnectFailure,
			isInstagramConnectPending,
			refreshSettings,
			putSettings,
			patchSettings,
			connectInstagram,
			clearInstagramConnectFailure,
			connectSquare,
			disconnectInstagram,
			disconnectSquare,
		}),
		[
			settings,
			isLoading,
			error,
			isInstagramConnected,
			isSquareConnected,
			instagramConnectFailure,
			isInstagramConnectPending,
			refreshSettings,
			putSettings,
			patchSettings,
			connectInstagram,
			clearInstagramConnectFailure,
			connectSquare,
			disconnectInstagram,
			disconnectSquare,
		],
	);

	return (
		<UserSettingsContext.Provider value={value}>
			{children}
		</UserSettingsContext.Provider>
	);
});
